import React, { useEffect, useMemo } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import DashboardSidebar from "../../../shared/layout/DashboardSidebar";
import DashboardScrollArea from "../../../shared/layout/DashboardScrollArea";
import ImpersonationBanner from "../../../shared/components/ImpersonationBanner";
import useEndImpersonation from "../../../shared/hooks/useEndImpersonation";
import { useTranslation } from "../../../i18n";
import useClientAccountStatus from "../hooks/useClientAccountStatus";
import { hydrateConnectionsFromServer } from "../platformConnectionsStorage";
import { formatTrialEndsAt } from "../../../lib/accountStatus";
import {
  IconDashboard,
  IconBilling,
  IconReviews,
  IconDeletionCases,
  IconEmailAlerts,
  IconPlatforms,
  IconTeam,
  IconPayment,
  IconSettings,
  IconSupport,
  IconDocumentation,
  IconPerks,
} from "../../../shared/icons/dashboardNavIcons";

const BASE = "/dashboard/client";

// Pages an expired account may still reach (subscribe, settings, help).
const EXPIRED_ALLOWED_PATHS = [
  `${BASE}/billing`,
  `${BASE}/payment`,
  `${BASE}/settings`,
  `${BASE}/support`,
];

const ClientDashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale } = useTranslation();
  const { endImpersonation, ending } = useEndImpersonation();
  const {
    isPremium: isPaid,
    isExpired,
    isTrial,
    trialEndsAt,
    loading: statusLoading,
  } = useClientAccountStatus();

  const trialEndLabel = useMemo(
    () => formatTrialEndsAt(trialEndsAt, locale),
    [trialEndsAt, locale]
  );

  // Always pull this client's connected platforms from the API (critical when
  // an admin impersonates — their browser has no localStorage for that company).
  useEffect(() => {
    hydrateConnectionsFromServer();
  }, []);

  const navItems = useMemo(
    () => [
      { label: t("nav.dashboard"), to: BASE, icon: <IconDashboard />, end: true },
      { label: t("nav.billing"), to: `${BASE}/billing`, icon: <IconBilling /> },
      ...(isPaid
        ? [
            {
              label: t("nav.premiumPerks"),
              to: `${BASE}/discounts`,
              icon: <IconPerks />,
            },
          ]
        : []),
      { label: t("nav.reviewHub"), to: `${BASE}/reviews`, icon: <IconReviews /> },
      {
        label: t("nav.deletionCases"),
        to: `${BASE}/deletion-cases`,
        icon: <IconDeletionCases />,
      },
      {
        label: t("nav.emailAlerts"),
        to: `${BASE}/email-alerts`,
        icon: <IconEmailAlerts />,
      },
      { label: t("nav.platforms"), to: `${BASE}/platforms`, icon: <IconPlatforms /> },
      { label: t("nav.team"), to: `${BASE}/team`, icon: <IconTeam /> },
      { label: t("nav.payment"), to: `${BASE}/payment`, icon: <IconPayment /> },
      { label: t("nav.settings"), to: `${BASE}/settings`, icon: <IconSettings /> },
    ],
    [t, isPaid]
  );

  const footerItems = useMemo(
    () => [
      { label: t("nav.support"), to: `${BASE}/support`, icon: <IconSupport /> },
      {
        label: t("nav.documentation"),
        to: `${BASE}/documentation`,
        icon: <IconDocumentation />,
      },
    ],
    [t]
  );

  return (
    <div className="min-h-screen bg-[#f4f6f7]">
      {/* Expired accounts are locked to billing/payment/settings/support. */}
      {!statusLoading &&
      isExpired &&
      !EXPIRED_ALLOWED_PATHS.some((p) => location.pathname.startsWith(p)) ? (
        <Navigate to={`${BASE}/billing`} replace />
      ) : null}
      <DashboardSidebar
        navItems={navItems}
        footerItems={footerItems}
        brandTitle={t("common.globalReputation")}
        brandSubtitle={t("common.brandSubtitle")}
        primaryAction={{
          label: t("nav.newCase"),
          onClick: () =>
            navigate("/dashboard/client/reviews?view=critical", {
              state: { hint: "select-for-deletion" },
            }),
        }}
      />
      <main className="ml-[264px] flex h-screen min-h-0 min-w-0 flex-col overflow-hidden">
        <ImpersonationBanner
          onEndImpersonation={endImpersonation}
          ending={ending}
        />
        {isTrial ? (
          <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-6 py-2.5">
            <p className="text-[13px] font-medium text-amber-900">
              {t("plans.trialBanner", {
                date: trialEndLabel.date,
                time: trialEndLabel.time,
              })}
            </p>
            <Link
              to={`${BASE}/billing`}
              className="shrink-0 rounded-full bg-amber-900 px-4 py-1.5 text-[12px] font-semibold text-white transition hover:bg-amber-800"
            >
              {t("plans.choosePlan")}
            </Link>
          </div>
        ) : null}
        {isExpired ? (
          <div className="flex items-center justify-between gap-3 border-b border-red-200 bg-red-50 px-6 py-2.5">
            <p className="text-[13px] font-medium text-red-900">
              {t("plans.expiredBanner")}
            </p>
            <Link
              to={`${BASE}/billing`}
              className="shrink-0 rounded-full bg-red-900 px-4 py-1.5 text-[12px] font-semibold text-white transition hover:bg-red-800"
            >
              {t("plans.choosePlan")}
            </Link>
          </div>
        ) : null}
        <DashboardScrollArea>
          <Outlet />
        </DashboardScrollArea>
      </main>
    </div>
  );
};

export default ClientDashboardLayout;

